import PropTypes from 'prop-types'
import { motion, AnimatePresence } from 'framer-motion'

import { Box } from '@redriverpress/system'

import usePrevious from '../hooks/usePrevious'

const MotionBox = motion(Box)
MotionBox.displayName = 'MotionBox'

export default function TransitionItems({
  animateInitial,
  children,
  currentItem,
  getKey,
  position,
}) {
  const prevPosition = usePrevious(position)

  function getDirection() {
    // If new position is a decrement (e.g. 2 -> 1)
    if (prevPosition > position) return 'prev'

    // New position is an increment
    return 'next'
  }

  const distance = '4rem'

  // `direction` needs to be passed into variants' enter/exit methods via
  // AnimatePresence AND MotionBox `custom` prop so that the value can be
  // updated *before* the actual enter/exit animation is computed.
  // https://www.framer.com/api/motion/animate-presence/#animatepresenceprops.custom
  const variants = {
    enter: dir => ({
      opacity: 0,
      x: dir === 'next' ? distance : `-${distance}`,
    }),
    centre: { opacity: 1, x: '0rem' },
    exit: dir => ({
      opacity: 0,
      x: dir === 'next' ? `-${distance}` : distance,
    }),
    transition: {
      type: 'tween',
      duration: 0.3,
      ease: 'easeInOut',
    },
  }

  return (
    <AnimatePresence
      exitBeforeEnter
      custom={getDirection()}
      initial={animateInitial}
    >
      {currentItem && (
        <MotionBox
          key={getKey(currentItem)}
          custom={getDirection()}
          variants={variants}
          initial='enter'
          animate='centre'
          exit='exit'
          transition={variants.transition}
        >
          {children}
        </MotionBox>
      )}
    </AnimatePresence>
  )
}

TransitionItems.defaultProps = {
  animateInitial: true,
  getKey: x => `${x.id}`,
  currentItem: null,
}

TransitionItems.propTypes = {
  animateInitial: PropTypes.bool,
  children: PropTypes.node.isRequired,
  currentItem: PropTypes.shape({ id: PropTypes.number.isRequired }),
  getKey: PropTypes.func,
  position: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
}
